import React from 'react';
import { useGate } from 'statsig-react';
import { styled, theme } from '@components-shared/src/stitches';
import { FeedPostInput } from '@components/Feed/FeedPostInput';
import {
  ScrollViewport,
  StyledScrollbar,
  StyledThumb,
} from '@components/main-components/containers/RadixScroll';
import { Chip } from '@components/main-components/elements/chips/Chip';
import { DeprecatedDynamicSkeleton } from '@components/main-components/skeleton/Skeleton';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { ActivityFeedGroupFilterOption } from '@graphql/reactQuery';
import { FEATURE_GATES } from '@utils/constants';
import { multiSelectOptionIsSelected, selectMultiSelectOption } from '@utils/helpers';
import { ChipContainer, RestyledScrollArea } from './shared';
import { FilteredActivityFeedTypes } from './store';

type FeedType = {
  type: ActivityFeedGroupFilterOption;
  label: string;
  icon?: React.ReactNode;
}[];

const FEED_TYPES: FeedType = [
  {
    type: ActivityFeedGroupFilterOption.All,
    label: 'All',
  },
  {
    type: ActivityFeedGroupFilterOption.Collected,
    label: 'Collected',
  },
  {
    type: ActivityFeedGroupFilterOption.AddedToPlaylist,
    label: 'Playlists',
  },
  {
    type: ActivityFeedGroupFilterOption.ReleaseDropped,
    label: 'Releases',
  },
];

type UserType = {
  type: FilteredActivityFeedTypes;
  label: string;
}[];

const FEED_USER_TYPES: UserType = [
  {
    type: FilteredActivityFeedTypes.NotableCollectors,
    label: 'Top Collectors',
  },
  {
    type: FilteredActivityFeedTypes.User,
    label: 'Following',
  },
];

const allFeedGroupOptions = Object.values(ActivityFeedGroupFilterOption);

const FilterSkeleton = () => <DeprecatedDynamicSkeleton width={64} height={32} borderRadius={6} />;

export const FeedHeader = ({
  activityFeedFilter,
  setActivityFeedFilter,
  activityFeedGroupFilter,
  setActivityFeedGroupFilter,
  loading,
}: {
  activityFeedFilter: FilteredActivityFeedTypes;
  setActivityFeedFilter: (selection: FilteredActivityFeedTypes) => void;
  activityFeedGroupFilter: ActivityFeedGroupFilterOption[];
  setActivityFeedGroupFilter: (selection: ActivityFeedGroupFilterOption[]) => void;
  loading: boolean;
}) => {
  const { isAuthenticatedWithAPI } = useAuth();
  const { login } = useConnectedAuth();

  const { value: activityFeedV2Enabled } = useGate(FEATURE_GATES.ACTIVITY_FEED_V2);

  return (
    <Container>
      <Header>
        <Title>Feed</Title>
        <Beta>Beta</Beta>
      </Header>

      <Wrapper>
        <Filter>
          <h3>Types</h3>
          <RestyledScrollArea>
            <ScrollViewport>
              <FilterContainer>
                {FEED_TYPES.map(({ type, label }) => (
                  <ChipContainer key={type}>
                    {loading ? (
                      <FilterSkeleton key={type} />
                    ) : (
                      <Chip
                        text={label}
                        isSelected={multiSelectOptionIsSelected({
                          prev: activityFeedGroupFilter || [],
                          currentSelected: type,
                          all: allFeedGroupOptions,
                          allKey: ActivityFeedGroupFilterOption.All,
                        })}
                        onClick={() => {
                          setActivityFeedGroupFilter(
                            selectMultiSelectOption({
                              prev: activityFeedGroupFilter || [],
                              currentSelected: type,
                              all: allFeedGroupOptions.filter(
                                option =>
                                  !(
                                    activityFeedFilter ===
                                      FilteredActivityFeedTypes.NotableCollectors &&
                                    option === ActivityFeedGroupFilterOption.ReleaseDropped
                                  ),
                              ),
                              allKey: ActivityFeedGroupFilterOption.All,
                            }),
                          );
                        }}
                        // Don't show Releases option for top collectors
                        disabled={
                          activityFeedFilter === FilteredActivityFeedTypes.NotableCollectors &&
                          type === ActivityFeedGroupFilterOption.ReleaseDropped
                        }
                      />
                    )}
                  </ChipContainer>
                ))}
              </FilterContainer>
            </ScrollViewport>
            <StyledScrollbar hiddenOnMobile orientation="horizontal">
              <StyledThumb hiddenOnMobile />
            </StyledScrollbar>
          </RestyledScrollArea>
        </Filter>

        <Filter>
          <h3>Users</h3>

          <FilterContainer>
            {FEED_USER_TYPES.map(({ type, label }) => (
              <ChipContainer key={type}>
                {loading ? (
                  <FilterSkeleton key={type} />
                ) : (
                  <Chip
                    text={label}
                    isSelected={activityFeedFilter === type}
                    onClick={() => {
                      setActivityFeedFilter(type);
                      if (type === FilteredActivityFeedTypes.User && !isAuthenticatedWithAPI) {
                        login();
                      }

                      const isReleasesSelected = multiSelectOptionIsSelected({
                        prev: activityFeedGroupFilter || [],
                        currentSelected: ActivityFeedGroupFilterOption.ReleaseDropped,
                        all: allFeedGroupOptions,
                        allKey: ActivityFeedGroupFilterOption.All,
                      });

                      // un select releases if selected when top collectors is chosen
                      if (
                        type === FilteredActivityFeedTypes.NotableCollectors &&
                        isReleasesSelected
                      ) {
                        setActivityFeedGroupFilter(
                          selectMultiSelectOption({
                            prev: activityFeedGroupFilter || [],
                            currentSelected: ActivityFeedGroupFilterOption.All,
                            all: allFeedGroupOptions,
                            allKey: ActivityFeedGroupFilterOption.All,
                          }),
                        );
                      }
                    }}
                  />
                )}
              </ChipContainer>
            ))}
          </FilterContainer>
        </Filter>
        {activityFeedV2Enabled && <FeedPostInput className="mt-2" />}
      </Wrapper>
    </Container>
  );
};

const Container = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  gap: 16,

  '@tablet': {
    gap: 24,
  },
});

const Header = styled('div', {
  display: 'flex',
  alignItems: 'center',
  gap: 16,

  '@tablet': {
    gap: 8,
  },

  '@desktop': {
    paddingX: 0,
  },
});

const Title = styled('h1', {
  title: 'L',

  '@tablet': {
    title: 'XL',
  },
});

const Beta = styled('div', {
  display: 'flex',
  base: 'XS',
  fontStyle: 'semibold',
  color: '$white',
  background: theme.gradients.fuchsia,
  borderRadius: 6,
  padding: '4px 6px',

  '@tablet': {
    padding: '7px 6px',
  },
});

const FilterContainer = styled('div', {
  display: 'flex',
  width: '100%',
  paddingBottom: 0,

  '@tablet': {
    paddingLeft: 16,
  },
});

const Wrapper = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: 8,

  '@tablet': {
    gap: 16,
  },

  '@desktop': {
    paddingLeft: 0,
  },
});

const Filter = styled('div', {
  display: 'flex',
  flexDirection: 'column',

  h3: {
    kicker: 'S',
    fontStyle: 'regular',
    textTransform: 'uppercase',
  },

  '@tablet': {
    flexDirection: 'row',
    alignItems: 'center',
  },
});
